import React, { useState, useEffect, useRef } from 'react'
import { Button, Card, Modal, Form, Select, Input } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import {
  paginationInit,
  formatMessageNew,
  initCurryingObj,
  downLoadFileBlob,
  initValueAndLabel,
  uniqueFun
} from '@/utils/common'
import { modalConfirm, modalError, messageModal } from '@/utils/modalUtils'
import { hasPermission } from '@/annotations/nike-react'
import {
  addData,
  updateData,
  downloadData,
  getFilterOptions,
  disableRecords,
  getTableData,
  getExceptionTypeList,
  previewDisable
} from '@/services/rfid/rfidConfig/specialTypeConfig'
import SearchBar from '@/components/searchbar/SearchBar'
import AntdTable from '@/components/table/AntdTable'
import { SPECIAL_TYPE_RECORD_STATUS } from '@/utils/types'

/** status 字段与展示文案映射 */
const statusObj = initCurryingObj(SPECIAL_TYPE_RECORD_STATUS)
const menuPerm = 'menu_rfid_configuration_specialType'
const SpecialTypeConfigPage = () => {
  const [newModalForm] = Form.useForm()
  const [tableList, setTableList] = useState([])
  const [queryFlag, setQueryFlag] = useState(false)
  const [pagination, setPagination] = useState(paginationInit)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [exceptionOptions, setExceptionOptions] = useState([])
  const [visibleFlag, setVisibleFlag] = useState(false)
  /** 避免首屏挂载时立刻请求 */
  const mounting = useRef(false)
  const formRef = useRef(null)
  /** 当前编辑行数据 */
  const editItemInfo = useRef({})
  /** SearchBar 筛选项配置（key 与请求 param 字段对齐） */
  const searchListInit = [
    {
      type: 'select',
      label: 'languageObj.specialTypeConfig.specialCode',
      key: 'specialCodeList',
      allowClear: true,
      mode: 'multiple',
      maxTagCount: 1,
      initialValues: [],
      selectList: [],
      spans: { xl: 8, xxl: 8 }
    },
    {
      type: 'select',
      label: 'languageObj.specialTypeConfig.specialType',
      key: 'specialTypeList',
      allowClear: true,
      mode: 'multiple',
      maxTagCount: 1,
      initialValues: [],
      selectList: [],
      spans: { xl: 8, xxl: 8 }
    },
    {
      type: 'select',
      label: 'languageObj.specialTypeConfig.exceptionType',
      key: 'exceptionTypeList',
      allowClear: true,
      mode: 'multiple',
      maxTagCount: 1,
      initialValues: [],
      selectList: [],
      spans: { xl: 8, xxl: 8 }
    },
    {
      type: 'select',
      label: 'languageObj.commonLocale.status',
      key: 'status',
      allowClear: true,
      mode: 'multiple',
      maxTagCount: 1,
      initialValues: [],
      selectList: SPECIAL_TYPE_RECORD_STATUS,
      spans: { xl: 8, xxl: 8 }
    }
  ]
  const [searchList, setSearchList] = useState(searchListInit)

  /** 打开弹窗：传 record 为编辑，不传为新增 */
  const addAndEditRecord = record => {
    setVisibleFlag(true)
    if (record && record.id) {
      editItemInfo.current = record
      newModalForm.setFieldsValue({
        specialCode: record.specialCode,
        specialType: record.specialType,
        exceptionTypeList: record.exceptionType?.split(',') || []
      })
    } else {
      editItemInfo.current = {}
      newModalForm.resetFields()
    }
  }

  const tableColumns = [
    {
      title: formatMessageNew('languageObj.specialTypeConfig.specialCode'),
      dataIndex: 'specialCode'
    },
    {
      title: formatMessageNew('languageObj.specialTypeConfig.specialType'),
      dataIndex: 'specialType'
    },
    {
      title: formatMessageNew('languageObj.specialTypeConfig.exceptionType'),
      dataIndex: 'exceptionTypeName'
    },
    {
      title: formatMessageNew('languageObj.commonLocale.status'),
      dataIndex: 'status',
      render: text => statusObj[text]
    },
    {
      title: formatMessageNew('languageObj.specialTypeConfig.lastUpdatedBy'),
      dataIndex: 'updateBy'
    },
    {
      title: formatMessageNew('languageObj.specialTypeConfig.lastUpdateTime'),
      dataIndex: 'updateTime'
    },
    {
      title: formatMessageNew('languageObj.commonLocale.operation'),
      dataIndex: 'operation',
      align: 'center',
      render: (_, record) => (
        <div>
          <Button
            disabled={!hasPermission(menuPerm) || record.status === 1}
            type="link"
            size="small"
            onClick={() => addAndEditRecord(record)}
          >
            {formatMessageNew('languageObj.commonLocale.edit')}
          </Button>
        </div>
      )
    }
  ]

  useEffect(() => {
    mounting.current && searchDataFun()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryFlag])

  useEffect(() => {
    getSelectOptions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /** 拉取筛选项下拉数据 */
  const getSelectOptions = () => {
    Promise.all([getExceptionTypeList(), getFilterOptions()]).then(res => {
      const next = [...searchListInit]
      next.forEach(item => {
        if (item.key === 'exceptionTypeList') {
          let list = initValueAndLabel(uniqueFun(res[0].resultData, 2), {
            value: 'code',
            label: 'name'
          })
          setExceptionOptions(list)
          item.selectList = list
        }
        const resDt = res[1].resultData || []
        if (item.key === 'specialCodeList') {
          const arr = resDt.find(item => item.type === 0) || { value: [] }
          item.selectList = initValueAndLabel(uniqueFun(arr.value, 2))
        }
        if (item.key === 'specialTypeList') {
          const arr = resDt.find(item => item.type === 1) || { value: [] }
          item.selectList = initValueAndLabel(uniqueFun(arr.value, 2))
        }
      })
      setSearchList(next)
    })
  }
  const getParams = () => {
    const info = (formRef.current && formRef.current.getSearchValue()) || {}
    let status = ''
    if (info.status && info.status.length === 1) {
      status = info.status[0]
    }
    return { ...info, status }
  }
  /** 导出 */
  const downLoadFun = () => {
    downloadData({ ...getParams() }).then(res => {
      downLoadFileBlob(res)
    })
  }

  const searchDataBtn = () => {
    mounting.current = true
    const pg = { ...pagination, currentPage: 1 }
    setPagination(pg)
    setQueryFlag(!queryFlag)
  }

  const searchClearDataBtn = () => {
    mounting.current = true
    formRef.current && formRef.current.clearForm()
    setSelectedRowKeys([])
    const pg = { ...pagination, currentPage: 1 }
    setPagination(pg)
    setQueryFlag(!queryFlag)
  }

  const handleTableChange = pagina => {
    const params = {
      ...pagination,
      currentPage: pagina.current,
      pageSize: pagina.pageSize
    }
    setSelectedRowKeys([])
    setPagination({ ...params })
    setQueryFlag(!queryFlag)
  }

  const searchDataFun = () => {
    const params = {
      pageNo: pagination.currentPage,
      size: pagination.pageSize,
      ...getParams()
    }

    getTableData(params).then(res => {
      const dt = res.resultData
      setTableList(dt.records || [])
      const pg = {
        ...pagination,
        total: dt.total
      }
      setPagination(pg)
    })
  }

  /** 确认禁用后调用接口 */
  const doDisable = () => {
    if (selectedRowKeys.length === 0) {
      modalError({ content: formatMessageNew('languageObj.specialTypeConfig.selectData') })
      return
    }
    disableRecords(selectedRowKeys[0]).then(() => {
      messageModal('success', {
        content: formatMessageNew('languageObj.commonLocale.success')
      })
      setSelectedRowKeys([])
      setQueryFlag(!queryFlag)
    })
  }

  /**
   * 禁用前预览：若存在关联 SKU，列表；否则简单二次确认。
   */
  const disabledDataFun = () => {
    if (selectedRowKeys.length === 0) {
      modalError({ content: formatMessageNew('languageObj.specialTypeConfig.selectData') })
      return
    }
    let specialCode = tableList.find(item => item.id === selectedRowKeys[0])?.specialCode
    previewDisable({ specialCode: specialCode })
      .then(res => {
        const list = res.resultData
        if (list.length > 0) {
          modalConfirm(
            {
              content: (
                <>
                  <div>{formatMessageNew('languageObj.specialTypeConfig.disableConfirmLinkedPrefix')}</div>
                  <div className="mt10">
                    {list.slice(0, 3).map((c, i) => (
                      <div key={i}>
                        <strong>{c.productCode}</strong> - {c.productName}
                      </div>
                    ))}
                    {list.length > 3 ? <div>...</div> : null}
                  </div>
                </>
              )
            },
            doDisable
          )
        } else {
          modalConfirm({ content: formatMessageNew('languageObj.specialTypeConfig.disableConfirmSimple') }, doDisable)
        }
      })
      .catch(() => {
        modalConfirm({ content: formatMessageNew('languageObj.specialTypeConfig.disableConfirmSimple') }, doDisable)
      })
  }

  /** 新增 / 编辑提交 */
  const handleOk = () => {
    newModalForm
      .validateFields()
      .then(values => {
        if (editItemInfo.current.id) {
          const params = {
            id: editItemInfo.current.id,
            ...values,
            exceptionType: values.exceptionTypeList.join(',')
          }
          delete params.exceptionTypeList
          updateData(params)
            .then(() => {
              messageModal('success', {
                content: formatMessageNew('languageObj.commonLocale.success')
              })
              setQueryFlag(!queryFlag)
              setVisibleFlag(false)
            })
            .catch(err => {
              const code = err.errorCode
              modalError({
                content: formatMessageNew(`languageObj.specialTypeConfig.errorcode_${code}`) || err.errorMessage
              })
            })
        } else {
          const params = {
            ...values,
            exceptionType: values.exceptionTypeList.join(',')
          }
          delete params.exceptionTypeList
          addData(params)
            .then(() => {
              messageModal('success', {
                content: formatMessageNew('languageObj.commonLocale.success')
              })
              setQueryFlag(!queryFlag)
              setVisibleFlag(false)
            })
            .catch(err => {
              const code = err.errorCode
              modalError({
                content: formatMessageNew(`languageObj.specialTypeConfig.errorcode_${code}`) || err.errorMessage
              })
            })
        }
      })
      .catch(() => {
        // modalError({
        //   content: formatMessageNew('languageObj.specialTypeConfig.mandatoryFields')
        // })
      })
  }

  const handleCancel = () => {
    setVisibleFlag(false)
  }

  const onSelectChange = keys => {
    setSelectedRowKeys(keys)
  }

  /** 已禁用行不可勾选*/
  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
    columnWidth: 50,
    onChange: onSelectChange,
    getCheckboxProps: record => ({
      disabled: record.status === 1
    })
  }

  const hasSelected = selectedRowKeys.length > 0
  return (
    <div>
      <Card bordered={false} bodyStyle={{ padding: 4 }}>
        <div className="search-bar">
          <SearchBar
            ref={formRef}
            searchList={searchList}
            showCollapseBtn={false}
            showSearchBtn={true}
            showClearBtn={true}
            onSearch={searchDataBtn}
            onClear={searchClearDataBtn}
          />
        </div>
        <div className="displayFlex alignCenter justifyContentSB mt10">
          {hasPermission(menuPerm) ? (
            <div className="displayFlex alignCenter">
              <Button type="primary" className="ml16" onClick={() => addAndEditRecord()}>
                <PlusOutlined />
                {formatMessageNew('languageObj.commonLocale.addNew')}
              </Button>
              <Button type="primary" danger disabled={!hasSelected} className="ml20" onClick={() => disabledDataFun()}>
                {formatMessageNew('languageObj.commonLocale.disable')}
              </Button>
            </div>
          ) : (
            <div className="auto-mt20"></div>
          )}
        </div>
        <div className="content-container overflow_visible">
          <AntdTable
            rowKey={record => record.id}
            dataSource={tableList}
            onDownload={downLoadFun}
            columns={tableColumns}
            pagination={pagination}
            onChange={handleTableChange}
            rowSelection={hasPermission(menuPerm) && rowSelection}
            // 独一无二的标识，不能重复
            columnsKey="columns_specialTypeConfig"
          />
        </div>
      </Card>
      <Modal
        open={visibleFlag}
        maskClosable={false}
        className="nike_modal"
        width="40%"
        title={
          editItemInfo.current.id
            ? formatMessageNew('languageObj.commonLocale.edit')
            : formatMessageNew('languageObj.commonLocale.addNew')
        }
        onOk={handleOk}
        onCancel={handleCancel}
        destroyOnClose
      >
        <Form form={newModalForm} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }} initialValues={{}}>
          <Form.Item
            name="specialCode"
            label={formatMessageNew('languageObj.specialTypeConfig.specialCode')}
            rules={
              editItemInfo.current.id
                ? []
                : [
                    {
                      required: true,
                      message: `${formatMessageNew('languageObj.commonLocale.placeholderInput')} ${formatMessageNew(
                        'languageObj.specialTypeConfig.specialCode'
                      )}`
                    }
                  ]
            }
          >
            <Input
              disabled={!!editItemInfo.current.id}
              placeholder={formatMessageNew('languageObj.commonLocale.placeholderInput')}
            />
          </Form.Item>
          <Form.Item
            name="specialType"
            label={formatMessageNew('languageObj.specialTypeConfig.specialType')}
            rules={[
              {
                required: true,
                message: `${formatMessageNew('languageObj.commonLocale.placeholderInput')} ${formatMessageNew(
                  'languageObj.specialTypeConfig.specialType'
                )}`
              }
            ]}
          >
            <Input placeholder={formatMessageNew('languageObj.commonLocale.placeholderInput')} />
          </Form.Item>
          <Form.Item
            name="exceptionTypeList"
            label={formatMessageNew('languageObj.specialTypeConfig.exceptionType')}
            rules={[
              {
                required: true,
                message: `${formatMessageNew('languageObj.commonLocale.placeholderSelect')} ${formatMessageNew(
                  'languageObj.specialTypeConfig.exceptionType'
                )}`
              }
            ]}
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              className="w100"
              placeholder={formatMessageNew('languageObj.commonLocale.placeholderSelect')}
              options={exceptionOptions}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes((input || '').toLowerCase())
              }
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default SpecialTypeConfigPage
